import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryWrapper } from 'app/types';
import { useSnakeBar } from '../../useSnakeBar';
import { toolsServices } from '../../../Domain/Tools/Services';
import type { PensioenbijMutation, PensioenbijMutationRaw } from './usePensioenbijEventsQuery';
import { normalizePensioenbijMutation } from './usePensioenbijEventsQuery';

type GetEmployeePensioenbijMutationsVariables = {
  employeeId: string;
  employerId: string;
  page?: number;
  pageSize?: number;
  status?: string[];
  searchQuery?: string;
  mutationType?: string;
};

type GetEmployeePensioenbijMutationsResultData = {
  data: PensioenbijMutation[];
  totalItems: number;
};

const queryFn =
  ({
    employeeId,
    employerId,
    page,
    pageSize,
    status,
    searchQuery,
    mutationType,
  }: GetEmployeePensioenbijMutationsVariables) =>
  async () => {
    const response = await toolsServices.getEmployeePensioenbijMutations({
      employeeId,
      employerId,
      page,
      pageSize,
      status,
      searchQuery,
      mutationType,
    });
    return response.data;
  };

export const EmployeePensioenbijMutationsQueryKey = 'getEmployeePensioenbijMutations';

export const useEmployeePensioenbijMutationsQuery: UseQueryWrapper<
  GetEmployeePensioenbijMutationsResultData,
  GetEmployeePensioenbijMutationsVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();
  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: EmployeePensioenbijMutationsQueryKey });
  }, [showErrorSnakeBar]);

  const select = React.useCallback(
    (data: { 'hydra:member': PensioenbijMutationRaw[]; 'hydra:totalItems': number }) => ({
      data: (data['hydra:member'] ?? []).map(normalizePensioenbijMutation),
      totalItems: data['hydra:totalItems'] ?? 0,
    }),
    [],
  );

  return useQuery<any>([EmployeePensioenbijMutationsQueryKey, ...Object.values(variables || {})], queryFn(variables), {
    select,
    onError,
    enabled: !!variables?.employeeId && !!variables?.employerId,
    ...options,
  });
};
